import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createSlots as _createSlots, normalizeClass as _normalizeClass, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "automation-wizard-condition-row" }
const _hoisted_2 = { class: "automation-wizard-condition-row__content" }
const _hoisted_3 = { class: "automation-wizard-condition-row__header" }
const _hoisted_4 = { class: "automation-wizard-condition-row__title typographies-paragraph-caps-small-bold" }
const _hoisted_5 = { class: "automation-wizard-condition-row__inline-row" }
const _hoisted_6 = { class: "automation-wizard-condition-row__if-label typographies-paragraph-medium-bold" }
const _hoisted_7 = { class: "automation-wizard-condition-row__checkboxes" }

import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { Translator } from 'types/injects/translator.type';
import {
    CF_TYPE_OPERATORS,
    ConditionOperator,
    ConditionScope,
    PROPERTY_TYPE_TO_CF_TYPE,
} from 'Dictionaries/automation/conditionOperators';
import { escapeHtml } from 'Helpers/htmlSanitizeHelper.js';
import { formatToDisplayWithCurrency } from 'utils/numberFormat';
import type {
    CfItem,
    Condition,
    ConditionField,
    FieldGroup,
    OperatorOption,
} from 'vcomponents/automation/templates/Wizard/Condition/types';

interface Props {
    groupedFields: FieldGroup[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AutomationWizardConditionRow',
  props: /*@__PURE__*/_mergeModels({
    groupedFields: {}
  }, {
    "modelValue": { required: true },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["remove"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const condition = _useModel<Condition>(__props, "modelValue");

const emit = __emit;

const $t = inject('$t') as Translator;

const store = useStore();

const translations: Record<string, string> = {
    configureTitle: $t.__('automation.AutomationWizardCondition.configure_title'),
    ifLabel: $t.__('common.labels.if'),
    fieldPlaceholder: $t.__('common.labels.select_field'),
    customFieldCaption: $t.__('common.labels.customfield'),
    operatorPlaceholder: $t.__('common.labels.select_condition'),
    valuePlaceholder: $t.__('common.labels.enter_value'),
    booleanYes: $t.__('common.labels.yes'),
    booleanNo: $t.__('common.labels.no'),
    cfUnavailable: $t.__('automation.AutomationWizardCondition.cf_unavailable'),
};

const allFields = computed((): ConditionField[] =>
    props.groupedFields.flatMap((group) => group.fields),
);

const selectedField = computed((): ConditionField | undefined =>
    allFields.value.find((field) => field.id === condition.value.id),
);

const isProperty = computed(
    (): boolean => condition.value.scope === ConditionScope.OBJECT_PROPERTIES,
);

const isFieldUnavailable = computed((): boolean => !!condition.value.id && !selectedField.value);

const dataType = computed((): string | null => condition.value.dataType || null);

const resolvedDataType = computed((): string | null => {
    const dt = dataType.value;
    if (!dt) {
        return null;
    }
    return PROPERTY_TYPE_TO_CF_TYPE[dt] || dt;
});

const selectedCfItems = computed((): CfItem[] => selectedField.value?.parameters?.items || []);

const isDate = computed((): boolean => resolvedDataType.value === 'date');
const isSelect = computed((): boolean => resolvedDataType.value === 'select');
const isCheckbox = computed((): boolean => resolvedDataType.value === 'checkbox');
const isNumeric = computed((): boolean => resolvedDataType.value === 'numeric');
const isBoolean = computed((): boolean => resolvedDataType.value === 'boolean');
const isInlineValueLayout = computed((): boolean => isDate.value || isNumeric.value);

const isValueLessOperator = computed<boolean>(
    () =>
        condition.value.operator === ConditionOperator.EMPTY ||
        condition.value.operator === ConditionOperator.NOT_EMPTY,
);

const numformat = computed(() => store.state.corp.numformat);

const currencySymbol = computed((): string | null => {
    if (dataType.value === 'amount') {
        return numformat.value?.currencysymbol || '€';
    }
    return null;
});

const showCurrencyPrefix = computed((): boolean => {
    if (!currencySymbol.value) {
        return false;
    }
    const pos = numformat.value?.currencypos;
    return pos === 'left' || pos === 'both';
});

const showCurrencySuffix = computed((): boolean => {
    if (dataType.value === 'percent') {
        return true;
    }
    if (!currencySymbol.value) {
        return false;
    }
    const pos = numformat.value?.currencypos;
    return pos === 'right' || pos === 'both' || !pos;
});

const numericSymbol = computed((): string | null => {
    if (dataType.value === 'percent') {
        return '%';
    }
    return currencySymbol.value;
});

const parsedValue = computed((): string | string[] | null => {
    const rawValue = condition.value.value;
    if (rawValue === null || rawValue === undefined) {
        return null;
    }
    if (isCheckbox.value) {
        try {
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return rawValue;
});

const serializeValue = (value: string | string[]): string => {
    if (isCheckbox.value && Array.isArray(value)) {
        return JSON.stringify(value);
    }
    return value as string;
};

const availableOperators = computed((): OperatorOption[] => {
    if (!resolvedDataType.value) {
        return [];
    }
    const operators = CF_TYPE_OPERATORS[resolvedDataType.value] || [];
    return operators.map((operator) => ({
        value: operator,
        label: $t.__(`common.operators.${operator}`),
    }));
});

const isConditionComplete = computed((): boolean => {
    if (!condition.value.id || !condition.value.operator) {
        return false;
    }
    if (isValueLessOperator.value) {
        return true;
    }
    const value = condition.value.value;
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (isCheckbox.value) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) && parsed.length > 0;
        } catch {
            return false;
        }
    }
    return true;
});

const formatDisplayValue = (value: string): string => {
    if (isBoolean.value) {
        return value === 'true' ? translations.booleanYes : translations.booleanNo;
    }
    if (isDate.value) {
        const dateFormat = store.state.corp.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        return moment(value).format(dateFormat);
    }
    if (isSelect.value) {
        const items = selectedCfItems.value;
        const item = items.find((cfItem) => String(cfItem.id) === String(value));
        return item ? item.label : value;
    }
    if (numericSymbol.value) {
        if (dataType.value === 'amount' && numformat.value) {
            const formatted = formatToDisplayWithCurrency(parseFloat(value), numformat.value, true);
            if (formatted !== false) {
                return String(formatted);
            }
        }
        return `${value} ${numericSymbol.value}`;
    }
    if (isCheckbox.value) {
        const items = selectedCfItems.value;
        try {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) {
                return value;
            }
            return parsed
                .map((itemId: string) => {
                    const item = items.find((cfItem) => String(cfItem.id) === String(itemId));
                    return item ? item.label : itemId;
                })
                .join(', ');
        } catch {
            return value;
        }
    }
    return value;
};

const conditionSummary = computed((): string => {
    if (!isConditionComplete.value || !selectedField.value) {
        return '';
    }
    const cfName = isProperty.value
        ? condition.value.label || selectedField.value.name
        : selectedField.value.name;
    const operatorLabel = $t.__(`common.operators.${condition.value.operator}`);

    if (isValueLessOperator.value) {
        const summaryKey = isProperty.value
            ? 'automation.AutomationWizardCondition.summary_property_no_value'
            : 'automation.AutomationWizardCondition.summary_no_value';
        return $t.__(summaryKey, { cfName, operator: operatorLabel });
    }

    const valueDisplay = escapeHtml(formatDisplayValue(condition.value.value!));
    const summaryKey = isProperty.value
        ? 'automation.AutomationWizardCondition.summary_property'
        : 'automation.AutomationWizardCondition.summary';
    return $t.__(summaryKey, {
        cfName,
        operator: operatorLabel,
        value: valueDisplay,
    });
});

const getDefaultValueForType = (effectiveType: string | null): string | null => {
    if (effectiveType === 'date') {
        return moment().format('YYYY-MM-DD');
    }
    if (effectiveType === 'boolean') {
        return 'true';
    }
    return null;
};

const handleFieldChange = (fieldId: string): void => {
    const field = allFields.value.find((item) => item.id === fieldId);
    if (!field) {
        return;
    }

    const fieldType = field.type;
    const effectiveType = PROPERTY_TYPE_TO_CF_TYPE[fieldType] || fieldType;
    const operators = CF_TYPE_OPERATORS[effectiveType] || [];
    const defaultOperator = operators[0] || null;

    condition.value = {
        ...condition.value,
        scope: field.isProperty ? ConditionScope.OBJECT_PROPERTIES : ConditionScope.CUSTOM_FIELDS,
        id: fieldId,
        dataType: fieldType,
        operator: defaultOperator,
        value: getDefaultValueForType(effectiveType),
        ...(field.isProperty
            ? { label: field.name, group: field.group }
            : { label: undefined, group: undefined }),
    };
};

const handleOperatorChange = (operator: string): void => {
    const wasValueLess = isValueLessOperator.value;
    const willBeValueLess =
        operator === ConditionOperator.EMPTY || operator === ConditionOperator.NOT_EMPTY;

    let value = condition.value.value;
    if (willBeValueLess) {
        value = null;
    } else if (wasValueLess) {
        value = getDefaultValueForType(resolvedDataType.value);
    }

    condition.value = { ...condition.value, operator, value };
};

const handleValueChange = (value: string | string[]): void => {
    condition.value = { ...condition.value, value: serializeValue(value) };
};

const handleBooleanChange = (boolValue: string): void => {
    condition.value = { ...condition.value, value: boolValue };
};

const isCheckboxItemSelected = (itemId: string): boolean => {
    const currentValues = parsedValue.value;
    if (!Array.isArray(currentValues)) {
        return false;
    }
    return currentValues.some((value) => String(value) === String(itemId));
};

const toggleCheckboxValue = (itemId: string): void => {
    const currentValues = Array.isArray(parsedValue.value) ? [...parsedValue.value] : [];
    const index = currentValues.findIndex((value) => String(value) === String(itemId));
    if (index >= 0) {
        currentValues.splice(index, 1);
    } else {
        currentValues.push(itemId);
    }
    handleValueChange(currentValues);
};

return (_ctx: any,_cache: any) => {
  const _component_CompassColoredIcon = _resolveComponent("CompassColoredIcon")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassOptionGroup = _resolveComponent("CompassOptionGroup")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassRadio = _resolveComponent("CompassRadio")!
  const _component_CompassRadioGroup = _resolveComponent("CompassRadioGroup")!
  const _component_CompassDatePicker = _resolveComponent("CompassDatePicker")!
  const _component_CompassCheckbox = _resolveComponent("CompassCheckbox")!
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_CompassColoredIcon, {
          icon: "filter",
          color: _ctx.CompassEnums.ColoredIconColorType.PURPLE
        }, null, 8 /* PROPS */, ["color"]),
        _createElementVNode("span", _hoisted_4, _toDisplayString(translations.configureTitle), 1 /* TEXT */),
        _createVNode(_component_CompassIconButton, {
          icon: "bin",
          type: _ctx.CompassEnums.IconButtonType.SECONDARY,
          variant: _ctx.CompassEnums.IconButtonVariant.DESTRUCTIVE,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('remove')))
        }, null, 8 /* PROPS */, ["type", "variant"])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("span", _hoisted_6, _toDisplayString(translations.ifLabel), 1 /* TEXT */),
        _createVNode(_component_CompassSelect, {
          modelValue: condition.value.id || '',
          filterable: "",
          menuClass: "select-automation-wizard",
          placeholder: translations.fieldPlaceholder,
          "onUpdate:modelValue": handleFieldChange
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.groupedFields, (group) => {
              return (_openBlock(), _createBlock(_component_CompassOptionGroup, {
                key: group.label,
                label: group.label
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.fields, (field) => {
                    return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                      key: field.id,
                      label: field.name,
                      labelCaption: field.isProperty ? '' : translations.customFieldCaption,
                      value: field.id
                    }, null, 8 /* PROPS */, ["label", "labelCaption", "value"]))
                  }), 128 /* KEYED_FRAGMENT */))
                ]),
                _: 2 /* DYNAMIC */
              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label"]))
            }), 128 /* KEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["modelValue", "placeholder"])
      ]),
      (condition.value.id)
        ? (_openBlock(), _createElementBlock("div", {
            key: condition.value.id,
            class: _normalizeClass(["automation-wizard-condition-row__fields", {
                    'automation-wizard-condition-row__fields--inline': isInlineValueLayout.value,
                }])
          }, [
            (isBoolean.value)
              ? (_openBlock(), _createBlock(_component_CompassRadioGroup, {
                  key: 0,
                  modelValue: condition.value.value,
                  label: "",
                  direction: "horizontal",
                  "onUpdate:modelValue": handleBooleanChange
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassRadio, {
                      label: translations.booleanYes,
                      modelValue: "true"
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_component_CompassRadio, {
                      label: translations.booleanNo,
                      modelValue: "false"
                    }, null, 8 /* PROPS */, ["label"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"]))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createVNode(_component_CompassSelect, {
                    modelValue: condition.value.operator,
                    menuClass: "select-automation-wizard",
                    placeholder: translations.operatorPlaceholder,
                    "onUpdate:modelValue": handleOperatorChange
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableOperators.value, (op) => {
                        return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                          key: op.value,
                          label: op.label,
                          value: op.value
                        }, null, 8 /* PROPS */, ["label", "value"]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["modelValue", "placeholder"]),
                  (condition.value.operator && !isValueLessOperator.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createCommentVNode(" Date "),
                        (isDate.value)
                          ? (_openBlock(), _createBlock(_component_CompassDatePicker, {
                              key: 0,
                              modelValue: parsedValue.value || '',
                              "onUpdate:modelValue": handleValueChange
                            }, null, 8 /* PROPS */, ["modelValue"]))
                          : (isSelect.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createCommentVNode(" Select (single) "),
                                _createVNode(_component_CompassSelect, {
                                  modelValue: parsedValue.value || '',
                                  menuClass: "select-automation-wizard",
                                  placeholder: translations.valuePlaceholder,
                                  "onUpdate:modelValue": handleValueChange
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedCfItems.value, (item) => {
                                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                                        key: item.id,
                                        label: item.label,
                                        value: item.id
                                      }, null, 8 /* PROPS */, ["label", "value"]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["modelValue", "placeholder"])
                              ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                            : (isCheckbox.value)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  _createCommentVNode(" Checkbox "),
                                  _createElementVNode("div", _hoisted_7, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedCfItems.value, (item) => {
                                      return (_openBlock(), _createBlock(_component_CompassCheckbox, {
                                        key: item.id,
                                        modelValue: item.id,
                                        label: item.label,
                                        checked: isCheckboxItemSelected(item.id),
                                        onChange: ($event: any) => (toggleCheckboxValue(item.id))
                                      }, null, 8 /* PROPS */, ["modelValue", "label", "checked", "onChange"]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ])
                                ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                              : (isNumeric.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                    _createCommentVNode(" Numeric "),
                                    _createVNode(_component_CompassInput, {
                                      modelValue: parsedValue.value || '',
                                      type: _ctx.CompassEnums.InputType.NUMBER,
                                      placeholder: translations.valuePlaceholder,
                                      "onUpdate:modelValue": handleValueChange
                                    }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                      (showCurrencyPrefix.value)
                                        ? {
                                            name: "prefix",
                                            fn: _withCtx(() => [
                                              _createElementVNode("span", null, _toDisplayString(numericSymbol.value), 1 /* TEXT */)
                                            ]),
                                            key: "0"
                                          }
                                        : undefined,
                                      (showCurrencySuffix.value)
                                        ? {
                                            name: "suffix",
                                            fn: _withCtx(() => [
                                              _createElementVNode("span", null, _toDisplayString(numericSymbol.value), 1 /* TEXT */)
                                            ]),
                                            key: "1"
                                          }
                                        : undefined
                                    ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "type", "placeholder"])
                                  ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                    _createCommentVNode(" Simple text "),
                                    _createVNode(_component_CompassInput, {
                                      modelValue: parsedValue.value || '',
                                      placeholder: translations.valuePlaceholder,
                                      "onUpdate:modelValue": handleValueChange
                                    }, null, 8 /* PROPS */, ["modelValue", "placeholder"])
                                  ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                      ], 64 /* STABLE_FRAGMENT */))
                    : _createCommentVNode("v-if", true)
                ], 64 /* STABLE_FRAGMENT */))
          ], 2 /* CLASS */))
        : _createCommentVNode("v-if", true),
      _createVNode(_Transition, { name: "fade-down" }, {
        default: _withCtx(() => [
          (isFieldUnavailable.value)
            ? (_openBlock(), _createBlock(_component_CompassAlert, {
                key: 0,
                type: _ctx.CompassEnums.AlertType.WARNING,
                description: translations.cfUnavailable,
                showClose: false
              }, null, 8 /* PROPS */, ["type", "description"]))
            : (isConditionComplete.value)
              ? (_openBlock(), _createBlock(_component_CompassAlert, {
                  key: 1,
                  type: _ctx.CompassEnums.AlertType.SUCCESS,
                  description: conditionSummary.value,
                  showClose: false
                }, null, 8 /* PROPS */, ["type", "description"]))
              : _createCommentVNode("v-if", true)
        ]),
        _: 1 /* STABLE */
      })
    ])
  ]))
}
}

})